import { LoadingSpinner } from '@momentum-ui/react-collaboration';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DispositionTypes, ICallHistoryRecord } from '../../../adapter-interfaces/src/ICallHistoryAdapter';
import {
  CallHistory,
  ICallHistoryItemProps,
  NoHistory
} from '../components/CallHistory';
import { CallRecapModal, RecapTab } from '../components/CallHistory/AISummary/CallRecapModal';
import { usePresenceContext } from '../contexts/PresenceContext';
import useCallHistory from '../hooks/useCallHistory';
import { useMakeCall } from '../hooks/useMakeCall';
import { usePresence } from '../hooks/usePresence';
import useWebexClasses from '../hooks/useWebexClasses';
import { EventType, eventEmitter } from '../utils/eventEmitter';
import { MobileCallHistory } from './MobileCallHistory';
import './WebexCallHistory.styles.scss';

export type IWebexCallHistoryProps = {
  updateMissedCallCount?: (unread: number) => void;
  userID: string;
  noHistoryMessage?: string | undefined;
  style?: React.CSSProperties;
  onPress?: (item: ICallHistoryItemProps) => void;
  extraCallHistoryItemProps?: ICallHistoryItemProps;
  isLocaleGerman?: boolean;
  labelUnknown?: string;
  isActiveTab?: boolean;
  testMode?: boolean;
  makeCall?: (address: string, isVideo?: boolean, label?: string) => void;
  mobileLayout?: boolean;
  mobileFilter?: 'all' | 'missed';
  hideMobileFilters?: boolean;
  onRestoreMobileFocus?: () => void;
  onMobileDetailOpenChange?: (isOpen: boolean) => void;
  presenceFeatureId?: string;
};

export interface IWebexCallHistoryHandle {
  refreshCallHistory: () => void;
  getLastUpdated: () => Date | undefined;
  postMissedCallCount: () => void;
}

type RecapModalState = {
  isOpen: boolean;
  containerId: string;
  callerName: string;
  startTime: string;
  durationSeconds: number;
  initialTab: RecapTab;
};

const INVALID_REMOTE_PARTY_ID = '00000000-0000-0000-0000-000000000000';

// Coalesce rapid visible-window and mobile filter churn into a single
// registration for the currently rendered set.
const PRESENCE_REGISTRATION_DEBOUNCE_MS = 120;

const normalizeRemotePartyId = (
  remotePartyId?: string
): string | undefined => {
  const normalizedId = remotePartyId?.trim();
  return normalizedId && normalizedId !== INVALID_REMOTE_PARTY_ID
    ? normalizedId
    : undefined;
};

const collectRemotePartyIds = (records: ICallHistoryRecord[]): string[] => {
  const userIds = new Set<string>();
  records.forEach(({ remotePartyId }) => {
    if (remotePartyId) {
      userIds.add(remotePartyId);
    }
  });
  return Array.from(userIds);
};

/**
 * @description The summary of this component.
 * @param {IWebexCallHistoryProps} obj - An object of props.
 * @param {string} obj.userID The ID of the current user
 * @param {string} obj.headerText The label for the header
 * @param {string} obj.noHistoryMessage The message when call history is empty
 * @param {Function} obj.onPress Handle when item is pressed
 * @param {Function} obj.onAudioCallPress Handle when audio call button is pressed
 * @param {Function} obj.onVideoCallPress Handle when video call button is pressed
 * @param {React.CSSProperties} obj.style Custom style for overriding this component's CSS
 * @returns {React.Component} WebexCallHistory component
 */
export const WebexCallHistory = forwardRef<
  IWebexCallHistoryHandle,
  IWebexCallHistoryProps
>(
  (
  { userID, style, noHistoryMessage, onPress, extraCallHistoryItemProps, isLocaleGerman, updateMissedCallCount, labelUnknown, isActiveTab, testMode = false, makeCall: makeCallProp, mobileLayout = false, mobileFilter, hideMobileFilters = false, onRestoreMobileFocus, onMobileDetailOpenChange, presenceFeatureId = 'callHistory' },
    ref
  ) => {
    const {
      callHistory = [],
      lastUpdated,
      refresh,
      loading,
      updateMissedCalls,
      deleteSingleCallHistoryRecord,
    } = useCallHistory(userID);

    // Initialize presence hook with feature ID
    const { getPresence, registerUsers } = usePresence(presenceFeatureId);
    // Get version from context to trigger re-renders when presence updates
    const { version: presenceVersion } = usePresenceContext();

    useImperativeHandle(ref, () => ({
      refreshCallHistory() {
        refresh();
      },
      getLastUpdated() {
        return lastUpdated;
      },
      postMissedCallCount() {
        updateMissedCalls()
      }
    }));
    const [adapterMakeCall] = useMakeCall();
    const makeCall = makeCallProp ?? adapterMakeCall;
    const isEmpty = callHistory?.length === 0;

    // Track visible indices from virtualized list
    const [visibleIndices, setVisibleIndices] = useState<number[]>([]);
    const [mobileFilteredItemIds, setMobileFilteredItemIds] = useState<
      string[]
    >([]);
    const mobileFilteredItemIdSet = useMemo(
      () => new Set(mobileFilteredItemIds),
      [mobileFilteredItemIds]
    );
    const registeredDesktopPresenceIds = useRef<Set<string>>(new Set());

    useEffect(() => {
      registeredDesktopPresenceIds.current.clear();
    }, [userID]);
    
    // Callback to receive visible indices from CallHistory component
    const handleVisibleItemsChange = useCallback((indices: number[]) => {
      setVisibleIndices(indices);
    }, []);

    const handleMobileFilteredItemsChange = useCallback((itemIds: string[]) => {
      setMobileFilteredItemIds((currentIds) =>
        currentIds.length === itemIds.length &&
        currentIds.every((id, index) => id === itemIds[index])
          ? currentIds
          : itemIds
      );
    }, []);

    const normalizedCallHistory = useMemo<ICallHistoryRecord[]>(
      () =>
        callHistory.map((record: ICallHistoryRecord) => ({
          ...record,
          remotePartyId: normalizeRemotePartyId(record.remotePartyId),
        })),
      [callHistory]
    );
    const allNormalizedRemotePartyIds = useMemo(
      () => new Set(collectRemotePartyIds(normalizedCallHistory)),
      [normalizedCallHistory]
    );

    const visibleUserIds = useMemo(
      () => {
        const records = mobileLayout
          ? normalizedCallHistory.filter(({ id }) =>
              mobileFilteredItemIdSet.has(id)
            )
          : visibleIndices.reduce<ICallHistoryRecord[]>((visible, index) => {
              const record = normalizedCallHistory[index];
              if (record) {
                visible.push(record);
              }
              return visible;
            }, []);
        const ids = collectRemotePartyIds(records);
        if (mobileLayout) {
          return ids;
        }
        const nextRegisteredDesktopPresenceIds = new Set(
          Array.from(registeredDesktopPresenceIds.current).filter((id) =>
            allNormalizedRemotePartyIds.has(id)
          )
        );
        ids.forEach((id) => nextRegisteredDesktopPresenceIds.add(id));
        return Array.from(nextRegisteredDesktopPresenceIds);
      },
      [
        allNormalizedRemotePartyIds,
        mobileFilteredItemIds,
        mobileFilteredItemIdSet,
        mobileLayout,
        normalizedCallHistory,
        visibleIndices,
      ]
    );

    useEffect(() => {
      if (!mobileLayout) {
        registeredDesktopPresenceIds.current = new Set(visibleUserIds);
      }
    }, [mobileLayout, visibleUserIds]);

    useEffect(() => {
      if (
        !mobileLayout &&
        visibleUserIds.length === 0 &&
        normalizedCallHistory.length > 0
      ) {
        return;
      }
      const registrationTimer = window.setTimeout(() => {
        registerUsers(visibleUserIds);
      }, PRESENCE_REGISTRATION_DEBOUNCE_MS);
      return () => {
        window.clearTimeout(registrationTimer);
      };
    }, [mobileLayout, normalizedCallHistory.length, registerUsers, visibleUserIds]);

    // Merge presence data into call history items
    // Force re-computation by calling getPresence for each record
    // This ensures UI updates when presence changes
    // BUG FIX: Include presenceVersion in dependencies so memo re-runs when presence updates
    const callHistoryWithPresence = useMemo(() => {
      return normalizedCallHistory.map((record: ICallHistoryRecord) => {
        const presence = record.remotePartyId ? getPresence(record.remotePartyId) : undefined;
        return {
          ...record,
          presenceType: presence?.presenceType,
          presenceLabel: presence?.presenceLabel,
        } as ICallHistoryItemProps;
      });
    }, [getPresence, normalizedCallHistory, presenceVersion]);
    

    // FIX #2: Fix eventEmitter memory leak - proper effect subscription with cleanup
    // Prevents multiple listeners and duplicate refresh calls
    useEffect(() => {
      eventEmitter.subscribe(
        EventType.AUTO_REFRESH,
        'autorefresh',
        refresh
      );
      return () => {
        eventEmitter.unsubscribe(EventType.AUTO_REFRESH, 'autorefresh');
      };
    }, [refresh]);
    const [cssClasses, sc] = useWebexClasses('call-history-widget', '', {
      loading,
      'is-empty': isEmpty && !loading,
    });

    const [selected, setSelected] = useState<ICallHistoryItemProps | undefined>(
      undefined
    );

    // Recap modal state
    const [recapModal, setRecapModal] = useState<RecapModalState | null>(null);

    const handleOpenRecapModal = useCallback((item: ICallHistoryItemProps, tab: RecapTab) => {
      const containerId = item.callingContainerIds?.[0];
      if (!containerId) return;
      setRecapModal({
        isOpen: true,
        containerId,
        callerName: item.name,
        startTime: item.startTime,
        durationSeconds: item.durationSeconds || 0,
        initialTab: tab,
      });
    }, []);

    const handleCloseRecapModal = useCallback(() => {
      setRecapModal(null);
    }, []);

    const dismissBagdeonClickRow = () => {
      updateMissedCalls()
    }

    useEffect(() => {
      if (updateMissedCallCount) {
        updateMissedCallCount(
          callHistory.filter((record) => !record.isRead && record.disposition === DispositionTypes.MISSED).length
        );
      }

    }, [callHistory, refresh, updateMissedCallCount]);

    return (
      <div className={cssClasses} style={style}>
        {loading ? (
          <LoadingSpinner scale={48} data-testid="loading-spinner" />
        ) : (
          <div className={sc('content')}>
            {isEmpty && !mobileLayout && (
              <div style={{ marginTop: '6rem' }}>
                <NoHistory message={noHistoryMessage} />
              </div>
            )}
            {mobileLayout && (
              <MobileCallHistory
                items={callHistoryWithPresence}
                isLocaleGerman={isLocaleGerman}
                labelUnknown={labelUnknown}
                makeCall={makeCall}
                onDeleteCall={deleteSingleCallHistoryRecord}
                onFilteredItemsChange={handleMobileFilteredItemsChange}
                filter={mobileFilter}
                hideFilters={hideMobileFilters}
                onRestoreParentFocus={onRestoreMobileFocus}
                onDetailOpenChange={onMobileDetailOpenChange}
                onItemPress={(item) => {
                  updateMissedCalls();
                  onPress?.(item);
                }}
              />
            )}
            {!isEmpty && !mobileLayout && (
              <CallHistory
                onPress={(item) => {
                  updateMissedCalls();
                  if (onPress) {
                    onPress(item);
                  }
                  setSelected(item);
                }}
                selectedItem={selected}
                items={callHistoryWithPresence}
                extraCallHistoryItemProps={extraCallHistoryItemProps}
                makeCall={makeCall}
                dismissBagdeonClickRow={dismissBagdeonClickRow}
                isLocaleGerman={isLocaleGerman}
                labelUnknown={labelUnknown}
                deleteSingleCallHistoryRecord={deleteSingleCallHistoryRecord}
                isActive={isActiveTab}
                testMode={testMode}
                onOpenRecapModal={handleOpenRecapModal}
                onVisibleItemsChange={handleVisibleItemsChange}
              />
            )}
          </div>
        )}

        {/* AI Summary Recap Modal */}
        {recapModal?.isOpen && (
          <CallRecapModal
            isOpen={true}
            onClose={handleCloseRecapModal}
            containerId={recapModal.containerId}
            callerName={recapModal.callerName}
            startTime={recapModal.startTime}
            durationSeconds={recapModal.durationSeconds}
            initialTab={recapModal.initialTab}
          />
        )}
      </div>
    );
  }
);

WebexCallHistory.defaultProps = {
  style: undefined,
  noHistoryMessage: 'Your recent calls will appear here.',
  onPress: undefined,
  extraCallHistoryItemProps: undefined,
};
